import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { BACKEND_URL } from '../api/axios';
import Navbar from './Navbar';
import { Link } from 'react-router-dom';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip,
  LineChart, Line, CartesianGrid,
} from 'recharts';
import { FileText, CalendarCheck, Award, Clock, Inbox } from 'lucide-react';
import { ShieldCheck, ShieldAlert, ShieldQuestion } from 'lucide-react';


const COLORS = { blue: '#7C3AED', orange: '#F59E0B', dark: '#0D1B3D', green: '#10B981' };

const GRADIENT_BUTTON =
  "bg-gradient-to-r from-purple-600 to-pink-500 text-white font-semibold rounded-full shadow-sm hover:opacity-90 transition cursor-pointer";

const GRADIENT_TEXT =
  "bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent";

const STAGE_LABELS = {
  applied: 'Applied',
  screening: 'Screening',
  shortlisted: 'Shortlisted',
  interview_round_1: 'Interview R1',
  interview_round_2: 'Interview R2',
  hr_round: 'HR Round',
  offer: 'Offer',
  joined: 'Joined',
};

// ---- Stat block click targets ----
// NOTE: no dedicated "all applications" / "all hires" / "all rejections" page
// exists in the route table today, so those three point at /employer/pipeline
// (closest match — shows candidates across stages). Adjust freely.
const STAT_ROUTES = {
  total_jobs: '/employer/jobs',
  total_applications: '/employer/pipeline',
  interviews_count: '/employer/interviews',
  offers_count: '/employer/offers',
   hires_count: '/employer/placements',
  rejections_count: '/employer/pipeline',
};

// ---- Recent Activity helpers ----
const ACTIVITY_META = {
  application: { icon: FileText, bg: 'bg-purple-50', fg: 'text-[#7C3AED]' },
  interview: { icon: CalendarCheck, bg: 'bg-amber-50', fg: 'text-[#F59E0B]' },
  offer: { icon: Award, bg: 'bg-emerald-50', fg: 'text-[#10B981]' },
};

const STATUS_STYLES = {
  pending: 'bg-gray-100 text-gray-600',
  scheduled: 'bg-purple-100 text-purple-700',
  completed: 'bg-emerald-100 text-emerald-700',
  accepted: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-600',
  extended: 'bg-amber-100 text-amber-700',
  cancelled: 'bg-gray-100 text-gray-500',
};

function StatusBadge({ status }) {
  if (!status) return null;
  const cls = STATUS_STYLES[status.toLowerCase()] || 'bg-gray-100 text-gray-600';
  return (
    <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold capitalize ${cls}`}>
      {status.replace(/_/g, ' ')}
    </span>
  );
}

function getInitials(name = '') {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0]?.toUpperCase())
    .join('');
}

function timeAgo(dateStr) {
  const date = new Date(dateStr);
  const diffMs = Date.now() - date.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function ActivityRow({ item }) {
  const meta = ACTIVITY_META[item.type] || ACTIVITY_META.application;
  const Icon = meta.icon;

  let title, subtitle;
  if (item.type === 'application') {
    title = `Applied for ${item.job_title}`;
  } else if (item.type === 'interview') {
    title = `${item.job_title} interview`;
    subtitle = item.status;
  } else if (item.type === 'offer') {
    title = 'Offer';
    subtitle = item.status;
  }

  return (
    <li className="flex items-center gap-3 py-3 px-2 rounded-lg hover:bg-purple-50/40 transition-colors">
      {/* Type icon badge */}
      <div className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${meta.bg}`}>
        <Icon size={16} className={meta.fg} strokeWidth={2.25} />
      </div>

      {/* Avatar initials */}
      <div className="shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-purple-600 to-pink-500 text-white text-xs font-semibold flex items-center justify-center">
        {getInitials(item.candidate_name) || '?'}
      </div>

      {/* Main content */}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-[#0D1B3D] truncate">
          {item.candidate_name}
          <span className="font-normal text-gray-500"> — {title}</span>
        </p>
      </div>

      {/* Status + time */}
      <div className="shrink-0 flex flex-col items-end gap-1">
        {subtitle && <StatusBadge status={subtitle} />}
        <span className="flex items-center gap-1 text-gray-400 text-xs">
          <Clock size={11} />
          {timeAgo(item.occurred_at)}
        </span>
      </div>
    </li>
  );
}

// Clickable stat block — same visual style as before, now a <button> that
// navigates to STAT_ROUTES[statKey] on click.
function StatBlock({ value, label, color, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group text-left rounded-xl -m-2 p-2 hover:bg-gradient-to-r hover:from-purple-600 hover:to-pink-500 hover:shadow-lg transition-all duration-200 cursor-pointer"
    >
      <p className="text-2xl font-bold group-hover:!text-white" style={{ color }}>{value}</p>
      <p className="text-xs text-gray-500 group-hover:text-white/90">{label}</p>
    </button>
  );
}


const VERIFICATION_META = {
  pending_verification: { label: 'Pending Verification', cls: 'bg-gray-100 text-gray-600', Icon: ShieldQuestion },
  documents_submitted: { label: 'Documents Submitted', cls: 'bg-[#FFFBEB] text-[#F59E0B]', Icon: ShieldQuestion },
  under_review: { label: 'Under Review', cls: 'bg-purple-50 text-[#7C3AED]', Icon: ShieldQuestion },
  verified: { label: 'Verified Employer', cls: 'bg-[#ECFDF5] text-[#10B981]', Icon: ShieldCheck },
  rejected: { label: 'Rejected', cls: 'bg-[#FEF2F2] text-[#EF4444]', Icon: ShieldAlert },
  resubmit_required: { label: 'Resubmit Required', cls: 'bg-[#FEF2F2] text-[#EF4444]', Icon: ShieldAlert },
};
 
function VerificationStatusCard({ verification }) {
  const meta = VERIFICATION_META[verification.status] || VERIFICATION_META.pending_verification;
  const Icon = meta.Icon;
  const isVerified = verification.status === 'verified';
 
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
      <div className="flex items-center gap-3">
        <div className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${meta.cls}`}>
          <Icon size={20} strokeWidth={2.25} />
        </div>
        <div>
          <p className="text-sm font-bold text-[#0D1B3D]">Verification Status</p>
          <span className={`inline-block mt-1 px-2.5 py-1 rounded-full text-xs font-semibold ${meta.cls}`}>
            {meta.label}
          </span>
          {isVerified && verification.verified_at && (
            <p className="text-xs text-gray-400 mt-1">
              Verified on {new Date(verification.verified_at).toLocaleDateString('en-IN')}
            </p>
          )}
        </div>
      </div>
 
      <Link
        to="/company/verification"
        className={`px-5 py-2 text-sm text-center !rounded-full ${GRADIENT_BUTTON}`}
      >
        {isVerified ? 'View Details' : 'Complete Verification'}
      </Link>
    </div>
  );
}

function EmployerDashboard() {
  const [user] = useState(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });

  const [stats, setStats] = useState(null);
  const [charts, setCharts] = useState(null);
  const [activity, setActivity] = useState([]);
  const [verification, setVerification] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    const load = async () => {
      try {
        const [statsRes, chartsRes, activityRes, verificationRes, walletRes] = await Promise.all([
          api.get('/jobs/employer/stats'),
          api.get('/employer/dashboard/charts'),
          api.get('/employer/dashboard/activity'),
          api.get('/company/verification-status'),
          api.get('/employer/wallet'),
        ]);
        setStats(statsRes.data);
        setCharts(chartsRes.data);
        setActivity(activityRes.data);
        setVerification(verificationRes.data);
        setWallet(walletRes.data);
      } catch (err) {
        console.error('Failed to fetch dashboard data', err);
      } finally {
        setLoading(false);
      }
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FAF5FC] to-[#FDF1F9]">
      <Navbar user={user} />

      <div className="max-w-7xl mx-auto px-4 py-6 sm:py-8 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className={`text-xl sm:text-2xl font-bold ${GRADIENT_TEXT}`}>Recruiter Dashboard</h2>
            <p className="text-gray-500 text-sm mt-1">Hiring activity across your company</p>
          </div>

          <Link
            to="/employer/jobs"
            className={`px-4 py-2.5 text-sm whitespace-nowrap ${GRADIENT_BUTTON}`}
          >
            💼 Manage Jobs
          </Link>
        </div>

        {/* Hiring Wallet summary */}
        <div className="rounded-2xl p-5 sm:p-6 bg-gradient-to-r from-purple-600 to-pink-500 shadow-sm">
          <p className="text-white/90 font-bold text-sm mb-4">💼 Hiring Wallet</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <p className="text-white/70 text-xs">Plan</p>
              <p className="text-white text-xl font-extrabold capitalize">
                {loading ? '—' : (wallet?.plan_name && wallet.plan_name.toLowerCase() !== 'free' ? wallet.plan_name : '—')}
              </p>
            </div>
            <div>
              <p className="text-white/70 text-xs">Job Posts Remaining</p>
              <p className="text-white text-xl font-extrabold">
                {loading ? '—' : (wallet?.job_posts_remaining === null ? 'Unlimited' : wallet?.job_posts_remaining)}
              </p>
            </div>
            <div>
              <p className="text-white/70 text-xs">Candidate Credits</p>
              <p className="text-white text-xl font-extrabold">
                {loading ? '—' : wallet?.candidate_credits}
              </p>
            </div>
            <div>
              <p className="text-white/70 text-xs">Featured Jobs</p>
              <p className="text-white text-xl font-extrabold">
                {loading ? '—' : wallet?.featured_jobs_remaining}
              </p>
            </div>
          </div>
        </div>

        {/* Stat blocks — each one navigates to STAT_ROUTES[key] on click */}

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          <StatBlock
            value={loading ? '—' : stats?.total_jobs}
            label="Jobs"
            color={COLORS.blue}
            onClick={() => navigate(STAT_ROUTES.total_jobs)}
          />
          <StatBlock
            value={loading ? '—' : stats?.total_applications}
            label="Applications"
            color={COLORS.orange}
            onClick={() => navigate(STAT_ROUTES.total_applications)}
          />
          <StatBlock
            value={loading ? '—' : stats?.interviews_count}
            label="Interviews"
            color={COLORS.blue}
            onClick={() => navigate(STAT_ROUTES.interviews_count)}
          />
          <StatBlock
            value={loading ? '—' : stats?.offers_count}
            label="Offers"
            color={COLORS.orange}
            onClick={() => navigate(STAT_ROUTES.offers_count)}
          />
          <StatBlock
            value={loading ? '—' : stats?.hires_count}
            label="Hires"
            color={COLORS.green}
            onClick={() => navigate(STAT_ROUTES.hires_count)}
          />
          <StatBlock
            value={loading ? '—' : stats?.rejections_count}
            label="Rejections"
            color="#6B7280"
            onClick={() => navigate(STAT_ROUTES.rejections_count)}
          />

          <StatBlock
  value={loading ? '—' : stats?.retentions_due_count}
  label="Retentions Due"
  color="#B45309"
  onClick={() => navigate('/employer/retentions')}
/>
        </div>

        {/* Verification Status */}
        {!loading && verification && (
          <VerificationStatusCard verification={verification} />
        )}

        {/* Quick Links */}
        <div className="flex flex-wrap gap-3">
          <Link to="/employer/interviews" className="bg-white rounded-full shadow-sm border border-gray-100 px-4 py-2.5 text-sm font-semibold text-purple-600 hover:bg-gradient-to-r hover:from-purple-600 hover:to-pink-500 hover:text-white hover:shadow-lg transition-all duration-200">
            📅 Manage Interviews
          </Link>
          <Link to="/employer/pipeline" className="bg-white rounded-full shadow-sm border border-gray-100 px-4 py-2.5 text-sm font-semibold text-purple-600 hover:bg-gradient-to-r hover:from-purple-600 hover:to-pink-500 hover:text-white hover:shadow-lg transition-all duration-200">
            🗂️ Candidate Pipeline
          </Link>
          <Link to="/employer/offers" className="bg-white rounded-full shadow-sm border border-gray-100 px-4 py-2.5 text-sm font-semibold text-purple-600 hover:bg-gradient-to-r hover:from-purple-600 hover:to-pink-500 hover:text-white hover:shadow-lg transition-all duration-200">
            📄 Offers
          </Link>
        
<Link to="/employer/placements" className="bg-white rounded-full shadow-sm border border-gray-100 px-4 py-2.5 text-sm font-semibold text-purple-600 hover:bg-gradient-to-r hover:from-purple-600 hover:to-pink-500 hover:text-white hover:shadow-lg transition-all duration-200">
  🎯 Placements
</Link>

<Link to="/employer/retentions" className="bg-white rounded-full shadow-sm border border-gray-100 px-4 py-2.5 text-sm font-semibold text-purple-600 hover:bg-gradient-to-r hover:from-purple-600 hover:to-pink-500 hover:text-white hover:shadow-lg transition-all duration-200">
  🌱 Retentions
</Link>
          <Link to="/employer/jobs" className="bg-white rounded-full shadow-sm border border-gray-100 px-4 py-2.5 text-sm font-semibold text-purple-600 hover:bg-gradient-to-r hover:from-purple-600 hover:to-pink-500 hover:text-white hover:shadow-lg transition-all duration-200">
            💼 Job Postings
          </Link>
          <Link to="/analytics/hiring" className="bg-white rounded-full shadow-sm border border-gray-100 px-4 py-2.5 text-sm font-semibold text-purple-600 hover:bg-gradient-to-r hover:from-purple-600 hover:to-pink-500 hover:text-white hover:shadow-lg transition-all duration-200">
            📊 Hiring Analytics
          </Link>
          <Link to="/analytics/job" className="bg-white rounded-full shadow-sm border border-gray-100 px-4 py-2.5 text-sm font-semibold text-purple-600 hover:bg-gradient-to-r hover:from-purple-600 hover:to-pink-500 hover:text-white hover:shadow-lg transition-all duration-200">
            📈 Job Analytics
          </Link>
          <Link to="/analytics/candidates" className="bg-white rounded-full shadow-sm border border-gray-100 px-4 py-2.5 text-sm font-semibold text-purple-600 hover:bg-gradient-to-r hover:from-purple-600 hover:to-pink-500 hover:text-white hover:shadow-lg transition-all duration-200">
            📈 Candidates Analytics
          </Link>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <h3 className="font-bold text-[#0D1B3D] text-sm mb-4">Applications per Day</h3>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={charts?.applications_per_day || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                <Tooltip />
                <Line type="monotone" dataKey="count" stroke={COLORS.blue} strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <h3 className="font-bold text-[#0D1B3D] text-sm mb-4">Hiring Funnel</h3>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart
                data={(charts?.hiring_funnel || []).map((s) => ({ ...s, label: STAGE_LABELS[s.stage] || s.stage }))}
                layout="vertical"
              >
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                <YAxis type="category" dataKey="label" width={110} tick={{ fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="count" fill={COLORS.orange} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <h3 className="font-bold text-[#0D1B3D] text-sm mb-4">Monthly Hiring</h3>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={charts?.monthly_hiring || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="count" fill={COLORS.blue} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <h3 className="font-bold text-[#0D1B3D] text-sm mb-4">Recruiter Performance</h3>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={charts?.recruiter_performance || []} layout="vertical">
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} />
                <YAxis type="category" dataKey="recruiter_name" width={110} tick={{ fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="interviews_conducted" fill={COLORS.dark} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-bold text-[#0D1B3D] text-sm">Recent Activity</h3>
            {!loading && activity.length > 0 && (
              <span className="text-xs text-gray-400">{activity.length} recent updates</span>
            )}
          </div>

          {loading ? (
            <ul className="divide-y divide-gray-100">
              {[...Array(4)].map((_, i) => (
                <li key={i} className="py-3 px-2 flex items-center gap-3 animate-pulse">
                  <div className="w-9 h-9 rounded-full bg-gray-100" />
                  <div className="w-8 h-8 rounded-full bg-gray-100" />
                  <div className="flex-1 h-3 bg-gray-100 rounded" />
                  <div className="w-14 h-3 bg-gray-100 rounded" />
                </li>
              ))}
            </ul>
          ) : activity.length === 0 ? (
            <div className="flex flex-col items-center justify-center text-center py-10 text-gray-400">
              <Inbox size={28} className="mb-2 opacity-60" />
              <p className="text-sm font-medium text-gray-500">No recent activity yet</p>
              <p className="text-xs mt-1">New applications, interviews, and offers will show up here.</p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {activity.map((item, i) => (
                <ActivityRow key={i} item={item} />
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

export default EmployerDashboard;